import { FC, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, HStack, Link, Stack } from '@chakra-ui/react';

import { TeamMemberRole } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCompanyProfile } from 'common/hooks/useCompanyProfile';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Heading from 'components/_core/Heading';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import Loader from 'components/Loader';
import SearchBar from 'components/SearchBar';
import * as Layout from 'layouts/ContentLayout';

import { useTeamMembers } from '../../hooks/useTeamMembers';
import useTeamMembersInvitationQuota from '../../hooks/useTeamMembersInvitationQuota';
import * as routes from '../../routes';
import { MembersTab } from '../../routes';
import { computeFilteredMembers, hasRole } from '../../utils';
import FilterBar from './components/FilterBar';
import ListHeading from './components/ListHeading';
import NoMemberView from './components/NoMemberView';
import TeamMembersList from './components/TeamMembersList';
import { useTabQueryParam } from './hooks/useTabQueryParam';
import locales from './locales';

const ROLES_DETAILS_URL =
  'https://help.shine.fr/fr/articles/6426418-quels-sont-les-differents-roles-et-acces-au-compte';

function getTranslatedDescription({
  isFreeRolesOnly,
}: {
  isFreeRolesOnly: boolean;
}): string {
  if (isFreeRolesOnly) {
    return locales.descriptionNoInvitations;
  }
  return locales.description;
}

// TODO(COP-1217) Refactor this page to simplify members filtering and avoid multiple conditional renderings
const Members: FC = () => {
  const { companyProfileId } = useCompanyContext();
  const { teamMembers, ...teamMembersQuery } = useTeamMembers(companyProfileId);
  const { company, ...companyQuery } = useCompanyProfile(companyProfileId);

  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const [selectedTab, setSelectedTab] = useTabQueryParam();

  const {
    canBuyExtraSeats,
    hasInvitationsLeft,
    invitationsLeft,
    teamMemberInvitationsQuota: { limit: invitationsLimit },
  } = useTeamMembersInvitationQuota();

  const isFreeRolesOnly = invitationsLimit === 1;

  const onSelectTab = (tab: MembersTab | null) => {
    setSelectedTab(tab);
    setError(null);
  };

  const filteredMembers = useMemo(
    () =>
      computeFilteredMembers({
        isFreeRolesOnly,
        query,
        selectedTab,
        teamMembers,
      }),
    [isFreeRolesOnly, query, selectedTab, teamMembers],
  );

  const hasMembers = filteredMembers.length > 0;

  const isOnlyAccountHolder =
    teamMembers.length === 1 &&
    !!teamMembers[0] &&
    hasRole(teamMembers[0], TeamMemberRole.AccountHolder);

  if (teamMembersQuery.loading || companyQuery.loading) {
    return <Loader />;
  }

  const description = getTranslatedDescription({
    isFreeRolesOnly,
  });

  return (
    <Layout.Root>
      <Layout.Body size="md">
        <Box paddingBottom="space-32">
          <HStack
            alignItems="center"
            justifyContent="space-between"
            marginBottom="space-8"
            spacing="space-8"
          >
            <Heading size="lg">{locales.title}</Heading>

            {!isOnlyAccountHolder ? (
              <Button
                as={RouterLink}
                icon="plus"
                to={routes.membersInvite}
                variant="secondary"
              >
                {isFreeRolesOnly
                  ? `${locales.inviteAccountant}`
                  : `${locales.inviteNewMember}`}
              </Button>
            ) : null}
          </HStack>

          <Text marginBottom="space-12" maxWidth="600px" size="lg">
            {locales.formatString(description, {
              companyName: company?.legalName || '',
            })}
          </Text>

          {isFreeRolesOnly ? null : (
            <Button
              as={Link}
              href={ROLES_DETAILS_URL}
              rel="noopener noreferrer"
              target="_blank"
              textDecoration="none"
              variant="inline-primary"
            >
              {locales.rolesLink}

              <SunshineIcon
                alignSelf="center"
                marginLeft="space-8"
                name="link"
              />
            </Button>
          )}
        </Box>

        <Stack
          direction={{
            base: 'column',
            lg: 'row',
          }}
          gap="space-16"
          spacing="space-16"
          width="100%"
        >
          <FilterBar
            isAccountantsOnly={isFreeRolesOnly}
            isOnlyAccountHolder={isOnlyAccountHolder}
            members={teamMembers}
            onSelectTab={onSelectTab}
            selectedTab={selectedTab}
          />

          <Box flex={1}>
            <SearchBar
              isDisabled={isOnlyAccountHolder}
              onChange={setQuery}
              value={query}
            />
          </Box>
        </Stack>

        {
          // shows empty state if there is only one member (account holder)
          (isOnlyAccountHolder && !selectedTab) ||
          // or we are searching through the search bar and there is no result
          (query !== '' && !hasMembers) ? (
            <NoMemberView
              ctaLabel={locales.inviteNewMember}
              description={locales.noMember.description}
              paddingTop="space-48"
              title={locales.noMember.title}
            />
          ) : null
        }

        {selectedTab && !hasMembers && query === '' ? (
          <NoMemberView
            ctaLabel={
              locales.formatString(locales.noMember.roleFiltered.inviteNew, {
                role: locales.noMember.roles[selectedTab],
              }) as string
            }
            paddingTop="space-64"
            title={
              locales.formatString(locales.noMember.roleFiltered.title, {
                role: locales.noMember.roles[selectedTab],
              }) as string
            }
          />
        ) : null}

        {!isOnlyAccountHolder && hasMembers ? (
          <Box paddingTop="space-32">
            {isFreeRolesOnly ? null : (
              <ListHeading
                description={
                  selectedTab === 'locked'
                    ? undefined
                    : locales.formatString(
                        locales.teamMembersList.description,
                        { count: invitationsLeft > 0 ? invitationsLeft : 0 },
                      )
                }
                title={locales.teamMembersList.title}
              />
            )}

            <TeamMembersList
              canUnlockMembers={hasInvitationsLeft || canBuyExtraSeats}
              hasInvitationsLeft={hasInvitationsLeft}
              isFreeRolesOnly={isFreeRolesOnly}
              onInvitationQuotaError={() =>
                setError(locales.errors.invitationQuotaError)
              }
              onUnlockUser={() => setSelectedTab(null)}
              teamMembers={filteredMembers}
            />
          </Box>
        ) : null}

        {error ? (
          <Callout marginTop="space-32" variant="error">
            {error}
          </Callout>
        ) : null}
      </Layout.Body>
    </Layout.Root>
  );
};

export default Members;
